import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { products } from '../../mocks/products';

const ProductDetailPage = () => {
  const { id } = useParams();
  const product = products.find((p) => p.slug === id);
  const [quantity, setQuantity] = useState(1);

  const relatedProducts = products
    .filter((p) => p.category === product?.category && p.id !== product?.id)
    .slice(0, 3);

  useEffect(() => {
    if (!product) return;

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://esbilgisayar.com.tr';

    document.title = `${product.name} - ${product.brand} | Es Donanım Bilgisayar`;

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', `${product.name} - ${product.condition} durumda, ${product.price.toLocaleString('tr-TR')} TL. Es Donanım Bilgisayar güvencesiyle 2. el bilgisayar satışı.`);
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', `${product.brand}, ${product.name}, 2. el ${product.category}, ikinci el bilgisayar, ${product.category} satışı`);
    }

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', `${siteUrl}/urunler/${product.slug}`);

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name,
      "image": product.image,
      "brand": {
        "@type": "Brand",
        "name": product.brand
      },
      "offers": {
        "@type": "Offer",
        "url": `${siteUrl}/urunler/${product.slug}`,
        "priceCurrency": "TRY",
        "price": product.price,
        "availability": "https://schema.org/InStock",
        "itemCondition": "https://schema.org/UsedCondition",
        "seller": {
          "@type": "Organization",
          "name": "Es Donanım Bilgisayar",
          "telephone": "+905065376985",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Anafartalar Cad. Ulus Şehir Çarşısı No:3/11",
            "addressLocality": "Altındağ",
            "addressRegion": "Ankara",
            "addressCountry": "TR"
          }
        }
      },
      "category": product.category,
      "sku": `ESDB-${product.id}`
    });
    document.head.appendChild(schemaScript);

    return () => {
      document.head.removeChild(schemaScript);
    };
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pb-16 lg:pb-0">
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-800 mb-4">Ürün Bulunamadı</h1>
              <Link to="/urunler" className="text-orange-600 hover:text-orange-700 font-medium">
                Ürünlere Dön
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleCall = () => {
    window.location.href = 'tel:+905065376985';
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      `Merhaba, ${product.name} hakkında bilgi almak istiyorum.`
    );
    window.open(`https://wa.me/905065376985?text=${message}`, '_blank');
  };

  const getCategoryName = (category: string) => {
    const categories: Record<string, string> = {
      laptop: 'Laptop',
      desktop: 'Masaüstü Bilgisayar',
      gaming: 'Oyuncu Bilgisayarı',
      monitor: 'Monitör',
    };
    return categories[category] || category;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <WhatsAppButton />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-200 pt-20 lg:pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-600 hover:text-orange-600 transition-colors">
              Ana Sayfa
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <Link to="/urunler" className="text-gray-600 hover:text-orange-600 transition-colors">
              Ürünler
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900 font-medium">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Product Detail */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="space-y-4">
            <div className="relative bg-gray-50 rounded-2xl overflow-hidden w-full h-[400px]">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              {product.status === 'new' && (
                <div className="absolute top-4 left-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  Yeni Geldi
                </div>
              )}
              {product.status === 'featured' && (
                <div className="absolute top-4 left-4 bg-orange-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  Öne Çıkan
                </div>
              )}
              {product.oldPrice && (
                <div className="absolute top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  İndirimli
                </div>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                  {getCategoryName(product.category)}
                </span>
                <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                  {product.brand}
                </span>
              </div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">{product.name}</h1>

              {/* Rating */}
              {product.rating && (
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`ri-star-${i < product.rating! ? 'fill' : 'line'} text-yellow-400 text-lg`}
                      ></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-600">({product.rating}.0)</span>
                </div>
              )}

              {/* Price */}
              <div className="flex items-baseline gap-3 mb-6">
                <span className="text-4xl font-bold text-orange-600">
                  {product.price.toLocaleString('tr-TR')} ₺
                </span>
                {product.oldPrice && (
                  <span className="text-2xl text-gray-400 line-through">
                    {product.oldPrice.toLocaleString('tr-TR')} ₺
                  </span>
                )}
              </div>

              {/* Condition */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-blue-600 text-xl"></i>
                  <div>
                    <p className="text-sm text-gray-600">Ürün Durumu</p>
                    <p className="font-semibold text-gray-900">{product.condition}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Specifications */}
            <div className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Teknik Özellikler</h2>
              <div className="space-y-3">
                {product.processor && (
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <i className="ri-cpu-line text-orange-600 text-xl"></i>
                      <span className="text-gray-600">İşlemci</span>
                    </div>
                    <span className="font-semibold text-gray-900">{product.processor}</span>
                  </div>
                )}
                {product.ram && (
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <i className="ri-database-2-line text-orange-600 text-xl"></i>
                      <span className="text-gray-600">RAM</span>
                    </div>
                    <span className="font-semibold text-gray-900">{product.ram}</span>
                  </div>
                )}
                {product.storage && (
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <i className="ri-hard-drive-2-line text-orange-600 text-xl"></i>
                      <span className="text-gray-600">Depolama</span>
                    </div>
                    <span className="font-semibold text-gray-900">{product.storage}</span>
                  </div>
                )}
                {product.gpu && (
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <i className="ri-gamepad-line text-orange-600 text-xl"></i>
                      <span className="text-gray-600">Ekran Kartı</span>
                    </div>
                    <span className="font-semibold text-gray-900">{product.gpu}</span>
                  </div>
                )}
                {product.specs && (
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <i className="ri-settings-3-line text-orange-600 text-xl"></i>
                      <span className="text-gray-600">Özellikler</span>
                    </div>
                    <span className="font-semibold text-gray-900">{product.specs}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Quantity Selector */}
            <div className="border-t border-gray-200 pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Adet</label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <i className="ri-subtract-line text-gray-600"></i>
                </button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 h-10 text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <i className="ri-add-line text-gray-600"></i>
                </button>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="border-t border-gray-200 pt-6 space-y-3">
              <button
                onClick={handleCall}
                className="w-full bg-orange-600 text-white py-4 rounded-xl font-semibold hover:bg-orange-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-phone-fill text-xl"></i>
                Hemen Ara: 0506 537 69 85
              </button>
              <button
                onClick={handleWhatsApp}
                className="w-full bg-green-500 text-white py-4 rounded-xl font-semibold hover:bg-green-600 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-whatsapp-fill text-xl"></i>
                WhatsApp ile Sor
              </button>
            </div>

            {/* Features */}
            <div className="border-t border-gray-200 pt-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-shield-check-line text-green-600 text-lg"></i>
                  <span>Garanti Kapsamında</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-truck-line text-green-600 text-lg"></i>
                  <span>Hızlı Teslimat</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-exchange-line text-green-600 text-lg"></i>
                  <span>Değişim Garantisi</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-customer-service-2-line text-green-600 text-lg"></i>
                  <span>7/24 Destek</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Benzer Ürünler</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Aynı kategoriden diğer kaliteli 2. el bilgisayar ve laptop modellerimize göz atın
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedProducts.map((relatedProduct) => (
                <div
                  key={relatedProduct.id}
                  className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="relative w-full h-64 overflow-hidden">
                    <img
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {relatedProduct.status === 'new' && (
                      <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                        Yeni
                      </div>
                    )}
                    {relatedProduct.status === 'featured' && (
                      <div className="absolute top-4 left-4 bg-orange-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                        Öne Çıkan
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                        {relatedProduct.brand}
                      </span>
                      <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                        {relatedProduct.condition}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">
                      {relatedProduct.name}
                    </h3>

                    <div className="space-y-2 mb-4">
                      {relatedProduct.processor && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <i className="ri-cpu-line text-orange-600"></i>
                          <span>{relatedProduct.processor}</span>
                        </div>
                      )}
                      {relatedProduct.ram && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <i className="ri-database-2-line text-orange-600"></i>
                          <span>{relatedProduct.ram}</span>
                        </div>
                      )}
                      {relatedProduct.storage && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <i className="ri-hard-drive-2-line text-orange-600"></i>
                          <span>{relatedProduct.storage}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-2xl font-bold text-orange-600">
                        {relatedProduct.price.toLocaleString('tr-TR')} ₺
                      </span>
                      {relatedProduct.oldPrice && (
                        <span className="text-sm text-gray-400 line-through">
                          {relatedProduct.oldPrice.toLocaleString('tr-TR')} ₺
                        </span>
                      )}
                    </div>

                    <Link
                      to={`/urunler/${relatedProduct.slug}`}
                      className="block w-full bg-orange-600 text-white text-center py-3 rounded-xl font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap"
                    >
                      Detayları Gör
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
